import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import "./planProductTools.css";
import { apiUrl } from "./apiBase";

export default function PlanProductParquetConverter() {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("");

  const convert = async (file) => {
    if (!file) return;
    setBusy(true);
    setStatus("Converting…");
    try {
      const form = new FormData();
      form.append("file", file);
      const res = await fetch(apiUrl("/api/convert/parquet"), {
        method: "POST",
        body: form,
      });
      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`${res.status} ${res.statusText}: ${txt}`);
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${file.name.replace(/\.[^.]+$/, "") || "converted"}.parquet`;
      a.click();
      window.URL.revokeObjectURL(url);
      setStatus("Done! Download should start automatically.");
    } catch (err) {
      console.error(err);
      setStatus("Conversion failed. Check console for details.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pp-page">
      <div className="pp-orb pp-orb--a" aria-hidden="true" />
      <div className="pp-orb pp-orb--b" aria-hidden="true" />
      <div className="pp-orb pp-orb--c" aria-hidden="true" />

      <header className="sticky top-0 z-30 h-16 border-b border-slate-800/70 bg-slate-950/35 backdrop-blur-xl flex items-center justify-between px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-sky-400 to-violet-500 grid place-items-center text-white font-bold shadow-lg shadow-sky-500/10">
            PQ
          </div>
          <div>
            <div className="font-bold text-lg leading-none">Parquet Converter</div>
            <div className="text-xs text-slate-400">Plan Product • Tools</div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Link to="/plan-product/consultant" className="pp-chip text-sm font-semibold">
            <span aria-hidden="true">🧠</span> Consultant
          </Link>
          <Link to="/plan-product-home" className="pp-chip text-sm font-semibold">
            <span aria-hidden="true">↩</span> Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10 relative">
        <div className="pp-glass pp-card pp-animate-in p-6 sm:p-8 w-full space-y-6">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-slate-900/60 border border-slate-700/70 grid place-items-center text-xl font-bold shadow-lg shadow-black/40">
              PQ
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">
                Parquet Converter
              </h1>
              <p className="text-slate-400 text-sm">
                Drag a file in, convert instantly, and download the Parquet output.
              </p>
            </div>
          </div>

          <div className="pp-glass pp-animate-in rounded-2xl p-5 border border-slate-800/70 space-y-4">
            <div className="grid sm:grid-cols-[1fr_auto] gap-3 items-start">
              <label className="block">
                <div className="text-xs text-slate-400 mb-2">File</div>
                <input
                  ref={inputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls,.txt,.parquet"
                  className="pp-input w-full"
                  disabled={busy}
                />
                <div className="text-xs text-slate-400 mt-2">
                  Supported: CSV, XLSX, XLS, TXT, Parquet
                </div>
              </label>

              <div className="sm:pt-7">
                <button
                  type="button"
                  className="pp-btn w-full sm:w-auto"
                  disabled={busy}
                  onClick={() => convert(inputRef.current?.files?.[0])}
                >
                  {busy ? "Converting…" : "Convert & Download"}
                </button>
              </div>
            </div>

            {status ? (
              <div className="text-sm text-slate-300">
                <span className="text-slate-400">Status:</span> {status}
              </div>
            ) : (
              <div className="text-sm text-slate-400">
                Tip: If your download doesn’t start, check the browser’s pop-up/download prompt.
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
